// The MIT License (MIT)

// Copyright (c) 2017 Zalando SE

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';

import {Technology} from '../technology';
import {RadarTechnology} from './radar-technology';
import {EvaluationLog} from '../evaluation-log';
import {Constants, FullRadarConstants} from '../constants';

import * as d3 from 'd3';
import {ActivatedRoute, Router} from '@angular/router';
import {WorkStream} from '../work-stream';
import {MatDialog} from '@angular/material/dialog';
import {TechRadarInfoDialogComponent} from './tech-radar-info-dialog/tech-radar-info-dialog.component';
import {TechnologyService} from '../technology.service';
import {TechnologyDetailsComponent} from '../technology-details/technology-details.component';
import {Location} from '@angular/common';
import {SearchService} from '../search.service';
import {TechFilterPipe} from '../utils/techFilter.pipe';

@Component({
  selector: 'app-tech-radar',
  templateUrl: './tech-radar.component.html',
  styleUrls: ['./tech-radar.component.scss']
})
export class TechRadarComponent implements OnInit, OnChanges {

  quadrants = Constants.quadrants;
  rings = Constants.rings;

  titleOffset = Constants.titleOffset;
  footerOffset = Constants.footerOffset;
  legendOffset = Constants.legendOffset;

  colors = Constants.colors;
  seed = Constants.seed;

  svgId = 'radar';

  width = Constants.width;
  height = Constants.height;

  title = Constants.title;

  // View specific constants
  rectangles = FullRadarConstants.rectangles;
  whiteRectangles = FullRadarConstants.whiteRectangles;
  whiteRectanglesInnerCircle = FullRadarConstants.whiteRectanglesInnerCircle;

  blips: any;
  hoveredTechnology?: RadarTechnology;

  segmented: any;

  searchString!: string;

  constructor(private technologyService: TechnologyService,
              private searchService: SearchService,
              private techFilter: TechFilterPipe,
              public dialog: MatDialog,
              private route: ActivatedRoute,
              private router: Router,
              private location: Location) {
  }

  @Input() technologies!: Technology[];

  @Input() workStream!: WorkStream;

  ngOnInit(): void {
    this.searchService.currentSearchString.subscribe(message => {
      // no input was given in search field
      if (message === '' && this.searchString === undefined) {
        return;
      }
      this.searchString = message;
      this.initTechRadar();

    });

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.openTechnologyDetailsDialog(id);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initTechRadar();
  }

  initTechRadar(): void {
    if (this.technologies !== undefined) {
      this.drawTechRadar(this.techFilter.transform(this.technologies, this.searchString));
    }
  }

  // HELPER FUNCTIONS
  random(): number {
    const x = Math.sin(this.seed++) * 10000;
    return x - Math.floor(x);
  }

  randomBetween(min: number, max: number): number {
    return min + this.random() * (max - min);
  }

  normalBetween(min: number, max: number): number {
    return min + (this.random() + this.random()) * 0.5 * (max - min);
  }

  polar(cartesian: any): any {
    const x = cartesian.x;
    const y = cartesian.y;

    return {
      t: Math.atan2(y, x),
      r: Math.sqrt(x * x + y * y)
    };
  }

  cartesian(polar: any): any {
    return {
      x: polar.r * Math.cos(polar.t),
      y: polar.r * Math.sin(polar.t)
    };
  }

  boundedInterval(value: number, min: number, max: number): number {
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    return Math.min(Math.max(value, low), high);
  }


  boundedRing(polar: { t: number; r: number; }, rMin: number, rMax: number): any {
    return {
      t: polar.t,
      r: this.boundedInterval(polar.r, rMin, rMax)
    };
  }

  boundedBox(point: { x: number; y: number; }, min: { x: number; y: number; }, max: { x: number; y: number; }): any {
    return {
      x: this.boundedInterval(point.x, min.x, max.x),
      y: this.boundedInterval(point.y, min.y, max.y)
    };
  }

  segment(quadrant: number, ring: number): any {
    const polarMin = {
      t: this.quadrants[quadrant].radial_min * Math.PI,
      r: ring === 0 ? 30 : this.rings[ring - 1].radius
    };

    const polarMax = {
      t: this.quadrants[quadrant].radial_max * Math.PI,
      r: this.rings[ring].radius
    };

    const cartesianMin = {
      x: 15 * this.quadrants[quadrant].factor_x,
      y: 15 * this.quadrants[quadrant].factor_y
    };

    const cartesianMax = {
      x: this.rings[3].radius * this.quadrants[quadrant].factor_x,
      y: this.rings[3].radius * this.quadrants[quadrant].factor_y
    };

    return {
      clipx: (d: { x: number; y: number; }) => {
        const c = this.boundedBox(d, cartesianMin, cartesianMax);
        const p = this.boundedRing(this.polar(c), polarMin.r + 15, polarMax.r - 15);
        d.x = this.cartesian(p).x; // adjust data too!
        return d.x;
      },
      clipy: (d: { y: number; x: number; }) => {
        const c = this.boundedBox(d, cartesianMin, cartesianMax);
        const p = this.boundedRing(this.polar(c), polarMin.r + 15, polarMax.r - 15);
        d.y = this.cartesian(p).y; // adjust data too!
        return d.y;
      },
      random: () => {
        return this.cartesian({
          t: this.randomBetween(polarMin.t, polarMax.t),
          r: this.normalBetween(polarMin.r, polarMax.r)
        });
      }
    };
  }

  translate = (x: number, y: number) => {
    return 'translate(' + x + ',' + y + ')';
  };

  viewbox(quadrant: number): string {
    return [
      Math.max(0, this.quadrants[quadrant].factor_x * 400) - 420,
      Math.max(0, this.quadrants[quadrant].factor_y * 400) - 420,
      440,
      440
    ].join(' ');
  }

  legendTransform(quadrant: number, ring: number, index = null): string {
    const dx = ring < 2 ? 0 : 120;
    let dy = (index == null ? -16 : index * 12);
    if (ring % 2 === 1) {
      dy = dy + 36 + this.segmented[quadrant][ring - 1].length * 12;
    }
    return this.translate(
      this.legendOffset[quadrant].x + dx,
      this.legendOffset[quadrant].y + dy
    );
  }

  showBubbleAndListItem = (technology: RadarTechnology) => {
    this.showBubble(technology);
    this.hoveredTechnology = technology;

  }

  hideBubbleAndListItem = () => {
    this.hideBubble();
  }

  showBubble = (d: RadarTechnology) => {
    if (d.active) {
      const tooltip = d3.select('#bubble text')
        .text(d.label);

      const bbox = (tooltip.node() as SVGSVGElement).getBBox();
      d3.select('#bubble')
        .attr('transform', this.translate(d.x - bbox.width / 2, d.y - 16))
        .style('opacity', 0.8);
      d3.select('#bubble rect')
        .attr('x', -5)
        .attr('y', -bbox.height)
        .attr('width', bbox.width + 10)
        .attr('height', bbox.height + 4);
      d3.select('#bubble path')
        .attr('transform', this.translate(bbox.width / 2 - 5, 3));
    }
  };

  hideBubble = () => {
    d3.select('#bubble')
      .attr('transform', this.translate(0, 0))
      .style('opacity', 0);
  };

  handleTechnologyClick = (d: Technology) => {
    this.location.go(`technologies/${this.workStream}/${d._id}`);
    this.openTechnologyDetailsDialog(d._id);
  };

  handleTechnologyClick2 = (e : any, d : any) => {
    this.handleTechnologyClick(d);
  };


  private openTechnologyDetailsDialog(id: string): void {
    this.technologyService.getTechnology(id).subscribe(
      technology => {
        const dialogRef = this.dialog.open(TechnologyDetailsComponent, {
          maxHeight: '85vh',
          minWidth: '70%',
          autoFocus: false,
          data: technology
        });
        dialogRef.afterClosed().subscribe(() => this.location.go(`technologies/${this.workStream}`));
      }
    );
  }

  ticked(): void {
    if (this.blips !== undefined) {
      this.blips.attr('transform', (d: { segment: { clipx: (arg0: any) => number; clipy: (arg0: any) => number; }; }) => this.translate(d.segment.clipx(d), d.segment.clipy(d)));
    }
  }

  drawTechRadar(technologies: Technology[]): void {

    const techRadarEntities = technologies.map(technology => this.convertTechnologyToRadarTechnology(technology));
    if (techRadarEntities.length > 0) {
      const hoveredId = this.hoveredTechnology?._id;
      if (hoveredId && !techRadarEntities.find((entry: RadarTechnology) => entry._id === hoveredId)) {
        this.hoveredTechnology = undefined;
      }
    }
    // partition entries according to segments
    this.segmented = new Array(4);
    for (let q = 0; q < 4; q++) {
      this.segmented[q] = new Array(4);
      for (let r = 0; r < 4; r++) {
        this.segmented[q][r] = [];
      }
    }

    for (const technology of techRadarEntities) {
      this.segmented[technology.quadrant][technology.ring].push(technology);
    }

    // assign unique sequential id to each entry
    let id = 1;
    for (let q = 0; q < 4; q++) {
      for (let r = 0; r < 4; r++) {
        const entriesInSegment = this.segmented[q][r];
        entriesInSegment.sort((a: { label: string; }, b: { label: string; }) => a.label.localeCompare(b.label));

        for (const entry of entriesInSegment) {
          entry.id = '' + id++;
        }
      }
    }

    d3.select('svg#' + this.svgId).selectAll('*').remove();

    const svg = d3.select('svg#' + this.svgId)
      .style('background-color', this.colors.background)
      .attr('width', this.width)
      .attr('height', this.height);

    const radar = svg.append('g');

    radar.attr('transform', this.translate(this.width / 2, this.height / 2));

    /*
    if ('zoomed_quadrant' in config) {
      svg.attr('viewBox', this.viewbox(config.zoomed_quadrant));
    } else {
      radar.attr('transform', translate(config.width / 2, config.height / 2));
    }
     */

    const grid = radar.append('g');

    // draw grid lines
    grid.append('line')
      .attr('x1', 0).attr('y1', -400)
      .attr('x2', 0).attr('y2', 400)
      .style('stroke', this.colors.grid)
      .style('stroke-width', 1);
    grid.append('line')
      .attr('x1', -400).attr('y1', 0)
      .attr('x2', 400).attr('y2', 0)
      .style('stroke', this.colors.grid)
      .style('stroke-width', 1);

    // draw rings
    for (const [i, ring] of this.rings.entries()) {
      if (i === this.rings.length - 1) {
        // draw rectangles which form 'cross'
        for (const r of this.rectangles) {
          grid.append('rect')
            .attr('x', r.x)
            .attr('y', r.y)
            .attr('width', r.width)
            .attr('height', r.height)
            .style('fill', r.color)
            .style('stroke', this.colors.grid)
            .style('stroke-width', 1)
          ;
        }

        // draw small white rectangles
        for (const r of this.whiteRectangles) {
          grid.append('rect')
            .attr('x', r.x)
            .attr('y', r.y)
            .attr('width', r.width)
            .attr('height', r.height)
            .style('fill', r.color)
          ;
        }

        // draw inner most circle - filled
        grid.append('circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', ring.radius)
          .style('fill', '#ffffff')
          .style('stroke', this.colors.grid)
          .style('stroke-width', 1);

        // draw small white rectangles around inner circle
        for (const r of this.whiteRectanglesInnerCircle) {
          grid.append('rect')
            .attr('x', r.x)
            .attr('y', r.y)
            .attr('width', r.width)
            .attr('height', r.height)
            .style('fill', r.color)
          ;
        }
      } else {
        grid.append('circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', ring.radius)
          .style('fill', 'none')
          .style('stroke', this.colors.grid)
          .style('stroke-width', 1);
      }

    }

    // draw text assigned to each circle
    for (const [i, r] of this.rings.entries()) {
      // left side
      grid.append('text')
        .attr('class', 'circleLegend')
        .text(r.name)
        .attr('x', -r.radius)
        .attr('y', 4)
        .attr('text-anchor', 'start')
        .style('fill', '#e5e5e5')
        .style('font-family', 'Roboto, Helvetica')
        .style('font-size', '15px')
        .style('font-weight', 'bold')
        .style('pointer-events', 'none')
        .style('user-select', 'none');

      // right side
      grid.append('text')
        .attr('class', 'circleLegend')
        .text(r.name)
        .attr('x', r.radius)
        .attr('y', 4)
        .attr('text-anchor', 'end')
        .style('fill', '#e5e5e5')
        .style('font-family', 'Roboto, Helvetica')
        .style('font-size', '15px')
        .style('font-weight', 'bold')
        .style('pointer-events', 'none')
        .style('user-select', 'none');
    }

    // draw text in quadrants
    for (const q of this.quadrants) {
      const text = grid.append('text')
        .attr('x', q.x)
        .attr('y', q.y)
        .style('font-family', 'Roboto, Helvetica')
        .style('font-weight', 'bold')
        .style('fill', this.colors.inactive)
        .attr('font-size', '34px');

      const quadrantTitle = this.getQuadrantName(q);

      for (const [i, word] of quadrantTitle.split(/[ ,]+/).entries()) {
        text.append('tspan')
          .attr('x', q.x)
          .attr('dy', 50 * i)
          .text(word)
          .attr('text-anchor', 'middle');
      }
    }


    // insert logo in the inner most circle
    grid.append('image')
      .attr('xlink:href', () => 'assets/AT_Logo_Black-orange.png')
      .attr('x', -40)
      .attr('y', -40)
      .attr('width', 100 * 0.8)
      .attr('height', 100 * 0.8);


    // layer for entries
    const rink = radar.append('g')
      .attr('id', 'rink');

    // rollover bubble (on top of everything else)
    const bubble = radar.append('g')
      .attr('id', 'bubble')
      .attr('x', 0)
      .attr('y', 0)
      .style('opacity', 0)
      .style('pointer-events', 'none')
      .style('user-select', 'none');
    bubble.append('rect')
      .attr('rx', 4)
      .attr('ry', 4)
      .style('fill', '#333');
    bubble.append('text')
      .style('font-family', 'sans-serif')
      .style('font-size', '10px')
      .style('fill', '#fff');
    bubble.append('path')
      .attr('d', 'M 0,0 10,0 5,8 z')
      .style('fill', '#333');

    // draw blips on radar
    this.blips = rink.selectAll('.blip')
      .data(techRadarEntities)
      .enter()
      .append('g')
      .attr('class', 'blip')
      .style('cursor', 'pointer')
      .on('mouseover', this.showBubbleAndListItem)
      .on('mouseout', this.hideBubbleAndListItem)
      .on('click', this.handleTechnologyClick2);

    // configure each blip
    this.blips.each(function(this:any, d:any): void {
      const blip = d3.select(this);

      // blip shape
      if (d.moved > 0 || d.moved < 0) {
        blip.append('circle')
          .attr('r', 12)
          .attr('fill', d.color)
          .style('opacity', 0.5);

        blip.append('circle')
          .attr('r', 9)
          .attr('fill', d.color);
      } else {
        blip.append('circle')
          .attr('r', 9)
          .attr('fill', d.color);
      }

      // blip text
      if (d.active) {
        const blipText = d.id;
        blip.append('text')
          .text(blipText)
          .attr('y', 3)
          .attr('text-anchor', 'middle')
          .style('fill', '#fff')
          .style('font-family', 'Roboto, Helvetica')
          .style('font-size', () => blipText.length > 2 ? '8px' : '11px')
          .style('pointer-events', 'none')
          .style('user-select', 'none');
      }
    });

    // distribute blips, while avoiding collisions
    d3.forceSimulation()
      .nodes(techRadarEntities)
      .velocityDecay(0.19) // magic number (found by experimentation)
      .force('collision', d3.forceCollide().radius(12).strength(0.85))
      .on('tick', () => this.blips.attr('transform', (d: { segment: { clipx: (arg0: any) => number; clipy: (arg0: any) => number; }; }) => this.translate(d.segment.clipx(d), d.segment.clipy(d))));
  }

  convertTechnologyToRadarTechnology(technology: Technology): RadarTechnology {
    const latestEvaluation = technology['evaluation-logs']
        .filter(evaluationLog => evaluationLog.date != null)
        .sort((a, b) =>
            new Date(b.date).getTime() - new Date(a.date).getTime())[0];

    const radarTechnology: RadarTechnology = {
      _id: technology._id,
      id: '',
      label: technology.name,
      quadrant: this.getQuadrantForTechnology(technology),
      ring: this.getRingForEvaluation(latestEvaluation?.evaluation),
      moved: this.getTendencyForTechnologyEvaluation(latestEvaluation),
      segment: 0,
      x: 0,
      y: 0,
      active: true,
      color: this.colors.inactive
    };

    radarTechnology.segment = this.segment(radarTechnology.quadrant, radarTechnology.ring);

    const point = radarTechnology.segment.random();

    radarTechnology.x = point.x;
    radarTechnology.y = point.y;
    radarTechnology.color = radarTechnology.active ? this.rings[radarTechnology.ring].color : this.colors.inactive;

    return radarTechnology;
  }

  getRingForEvaluation(evaluation: string): number {
    if (evaluation === null) {
      return 3;
    }

    switch (evaluation.toLowerCase()) {
      case 'adopt':
        return 0;
      case 'trial':
        return 1;
      case 'assess':
        return 2;
      case 'hold':
        return 3;
      default:
        return 3;
    }
  }

  getTendencyForTechnologyEvaluation(evaluation: EvaluationLog): number {

    if (evaluation.tendency === '' || evaluation.tendency === null) {
      return 0;
    } else if (this.getRingForEvaluation(evaluation.tendency) > this.getRingForEvaluation(evaluation.evaluation)) {
      return -1;
    } else {
      return 1;
    }
  }

  getQuadrantForTechnology(technology: Technology): number {

    let result: number;
    const normalizedCategory = (technology.category || '').replace(/\u00A0/g, ' ').trim();

    if (technology['work-stream'] === 'Machine Learning') {
      result = this.quadrants.findIndex(quadrant => quadrant.quadrant_name.replace(/\u00A0/g, ' ').trim() === normalizedCategory);
    } else if (technology['work-stream'] === 'Agentic AI') {
      result = this.quadrants.findIndex(quadrant => quadrant.agentic_name.replace(/\u00A0/g, ' ').trim() === normalizedCategory);
    } else if (technology['work-stream'] === 'Data Engineering') {
      result = this.quadrants.findIndex(quadrant => quadrant.de_name.replace(/\u00A0/g, ' ').trim() === normalizedCategory);
    } else {
      result = this.quadrants.findIndex(quadrant => quadrant.dv_name.replace(/\u00A0/g, ' ').trim() === normalizedCategory);
    }

    if (result === -1) {
      result = 0;
    }

    return result;
  }

  getQuadrantName(quadrant: any): string {
    switch (this.workStream) {
      case WorkStream.ML:
        return quadrant.quadrant_name;
      case WorkStream.AI:
        return quadrant.agentic_name;
      case WorkStream.DE:
        return quadrant.de_name;
      case WorkStream.DV:
        return quadrant.dv_name;
      default:
        return quadrant.quadrant_name;
    }
  }

  getQuadrantNames(): string[] {
    return this.quadrants.map(quadrant => this.getQuadrantName(quadrant));
  }

  getValidRings(): any[] {
    return this.rings.filter(ring => ring.name !== '');
  }

  onMouseOverLegendEntry(technology: RadarTechnology): void {
    this.showBubble(technology);
  }

  onMouseLeaveLegendEntry(): void {
    this.hideBubble();
  }

  openInfoDialog(): void {
    this.dialog.open(TechRadarInfoDialogComponent, {data: this.workStream, width: '53vw', height: '70%'});
  }
}
